/**
 * PIP (Performance Improvement Plan) — T12 of the Performance Reviews plan
 * (`docs/plans/performance-management/performance-reviews-plan.md`, "PIP" section). One flat
 * router, `improvementPlanContract` (`/improvement-plans`).
 *
 * SELF-CONTAINED: its own workflow (`entityType: 'improvement_plan'`, an HR countersign step — a
 * PIP has employment-termination consequences, so it must not be a manager acting alone), own
 * lifecycle, own tables. Not a `PerformanceReview` status — a PIP can start outside a review cycle
 * and must survive cycle closure.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  commentOnlyDecisionBodySchema,
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const improvementPlanStatusSchema = z.enum([
  'draft',
  'active',
  'extended',
  'completed_successful',
  'completed_unsuccessful',
  'cancelled',
]);
export const milestoneStatusSchema = z.enum(['open', 'done', 'cancelled']);

export const improvementPlanMilestoneSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  planId: uuidSchema,
  title: z.string(),
  dueOn: dateOnlySchema.nullable(),
  status: milestoneStatusSchema,
  evidence: z.string().nullable(),
  verifierPersonId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type ImprovementPlanMilestone = z.infer<typeof improvementPlanMilestoneSchema>;

export const improvementPlanSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  personName: z.string(),
  reviewId: uuidSchema.nullable(),
  managerPersonId: uuidSchema,
  reason: z.string(),
  startsOn: dateOnlySchema,
  endsOn: dateOnlySchema,
  status: improvementPlanStatusSchema,
  workflowInstanceId: uuidSchema.nullable(),
  milestones: z.array(improvementPlanMilestoneSchema),
  createdAt: instantSchema,
});
export type ImprovementPlan = z.infer<typeof improvementPlanSchema>;

export const listPlansQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  managerPersonId: uuidSchema.optional(),
  status: improvementPlanStatusSchema.optional(),
});

export const createPlanBodySchema = z
  .object({
    personId: uuidSchema,
    reviewId: uuidSchema.optional(),
    reason: z.string().trim().min(1).max(2000),
    startsOn: dateOnlySchema,
    endsOn: dateOnlySchema,
    milestones: z
      .array(
        z.object({ title: z.string().trim().min(1).max(200), dueOn: dateOnlySchema.optional() }),
      )
      .max(20)
      .optional(),
  })
  .refine((body) => body.endsOn >= body.startsOn, {
    message: 'endsOn must be on or after startsOn',
    path: ['endsOn'],
  });

export const updatePlanBodySchema = z.object({
  reason: z.string().trim().min(1).max(2000).optional(),
  /** Editable while the plan is a draft. On a running plan a new end date is an extension — use
   * `extendPlan`, which takes the reason (409 here). */
  endsOn: dateOnlySchema.optional(),
  /**
   * @deprecated Use the explicit transition routes (`extendPlan`, `closePlan`, `cancelPlan`); the
   * server applies the same rules here and rejects illegal transitions (409). Still accepted so
   * installed mobile builds keep working (performance remediation R5); removed after the next
   * mobile release.
   */
  status: z
    .enum(['completed_successful', 'completed_unsuccessful', 'cancelled', 'extended'])
    .optional()
    .describe(
      'deprecated: use the explicit transition routes; the server rejects illegal transitions',
    ),
});

export const decidePlanBodySchema = commentOnlyDecisionBodySchema;

export const addMilestoneBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  dueOn: dateOnlySchema.optional(),
});

export const updateMilestoneBodySchema = z.object({
  /**
   * @deprecated Use `verifyMilestone` (done) — the server applies the same verifier rule here: only
   * the assigned verifier may mark a milestone done, and the verifier is always the caller. `open`
   * → `cancelled` stays a plain manage write; anything else is 409. Still accepted so installed
   * mobile builds keep working (performance remediation R5).
   */
  status: milestoneStatusSchema
    .optional()
    .describe('deprecated: use verifyMilestone; the server rejects illegal transitions'),
  evidence: z.string().trim().max(2000).optional(),
  /** Assigns who will verify this milestone: the plan's manager, the subject's reporting or
   * skip-level manager, or an HR holder of improvement_plan:manage over the subject (else 422).
   * `listVerifierCandidates` returns exactly that set. */
  verifierPersonId: uuidSchema.optional(),
});

/** New end date strictly later than the current one; the reason is recorded in the audit log. */
export const extendPlanBodySchema = z.object({
  endsOn: dateOnlySchema,
  reason: z.string().trim().min(1).max(2000),
});
export type ExtendPlanBody = z.infer<typeof extendPlanBodySchema>;

export const improvementPlanOutcomeSchema = z.enum([
  'completed_successful',
  'completed_unsuccessful',
]);

/** The outcome comment is required (plan §5J) — it is what HR reads before any next step. */
export const closePlanBodySchema = z.object({
  outcome: improvementPlanOutcomeSchema,
  comment: z.string().trim().min(1).max(2000),
});
export type ClosePlanBody = z.infer<typeof closePlanBodySchema>;

export const cancelPlanBodySchema = z.object({
  reason: z.string().trim().min(1).max(2000),
});
export type CancelPlanBody = z.infer<typeof cancelPlanBodySchema>;

/** The plan's subject records what they did towards a milestone (N24). Never marks it done. */
export const submitMilestoneEvidenceBodySchema = z.object({
  evidence: z.string().trim().min(1).max(2000),
});
export type SubmitMilestoneEvidenceBody = z.infer<typeof submitMilestoneEvidenceBodySchema>;

/** No verifier id in the body: the verifier is the caller, set by the server. */
export const verifyMilestoneBodySchema = z.object({});

export const verifierCandidateSchema = z.object({
  personId: uuidSchema,
  name: z.string(),
  /** Why this person may verify: the plan's manager, the subject's reporting manager, their
   * skip-level manager, or HR holding improvement_plan:manage over the subject. */
  relation: z.enum(['plan_manager', 'reporting_manager', 'skip_level', 'hr']),
});
export type VerifierCandidate = z.infer<typeof verifierCandidateSchema>;

export const improvementPlanContract = c.router(
  {
    listPlans: {
      method: 'GET',
      path: '/',
      query: listPlansQuerySchema,
      responses: {
        200: paginatedSchema(improvementPlanSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List PIPs in the caller's visible scope",
      metadata: routeMeta('improvement_plan:read'),
    },

    getPlan: {
      method: 'GET',
      path: '/:planId',
      responses: { 200: improvementPlanSchema, 401: errorBodySchema, 404: errorBodySchema },
      summary: 'Get one PIP with its milestones',
      metadata: routeMeta('improvement_plan:read'),
    },

    createPlan: {
      method: 'POST',
      path: '/',
      body: createPlanBodySchema,
      responses: {
        201: improvementPlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Open a PIP for a report — improvement_plan:manage, manager+ only; 409 when the person already has a draft/active/extended plan',
      metadata: routeMeta('improvement_plan:manage'),
    },

    updatePlan: {
      method: 'PATCH',
      path: '/:planId',
      body: updatePlanBodySchema,
      responses: {
        200: improvementPlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Update a PIP's reason (any non-terminal plan) or end date (draft only); the deprecated status field follows the extend/close/cancel rules",
      metadata: routeMeta('improvement_plan:manage'),
    },

    submitPlan: {
      method: 'POST',
      path: '/:planId/submit',
      body: z.object({}),
      responses: {
        200: improvementPlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Starts the HR-countersign WorkflowInstance for this PIP',
      metadata: routeMeta('improvement_plan:manage'),
    },

    approvePlan: {
      method: 'POST',
      path: '/:planId/approve',
      body: decidePlanBodySchema,
      responses: {
        200: improvementPlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve the step currently awaiting the caller (manager, then HR countersign)',
      metadata: routeMeta(null),
    },

    rejectPlan: {
      method: 'POST',
      path: '/:planId/reject',
      body: decidePlanBodySchema,
      responses: {
        200: improvementPlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject the step currently awaiting the caller',
      metadata: routeMeta(null),
    },

    addMilestone: {
      method: 'POST',
      path: '/:planId/milestones',
      body: addMilestoneBodySchema,
      responses: {
        201: improvementPlanMilestoneSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Add a milestone to a PIP (409 once the plan is completed or cancelled)',
      metadata: routeMeta('improvement_plan:manage'),
    },

    updateMilestone: {
      method: 'PATCH',
      path: '/:planId/milestones/:milestoneId',
      body: updateMilestoneBodySchema,
      responses: {
        200: improvementPlanMilestoneSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Update a milestone's evidence/verifier (verifier must be an allowed person, else 422); deprecated status follows verifyMilestone's rule",
      metadata: routeMeta('improvement_plan:manage'),
    },

    // --- Explicit PIP transitions (performance remediation §5J, T4.5) — replace status writes. ---

    extendPlan: {
      method: 'POST',
      path: '/:planId/extend',
      body: extendPlanBodySchema,
      responses: {
        200: improvementPlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Extend an active/extended PIP to a later end date (422 unless later than the current one; 409 from any other status). Requires Idempotency-Key',
      metadata: routeMeta('improvement_plan:manage'),
    },

    closePlan: {
      method: 'POST',
      path: '/:planId/close',
      body: closePlanBodySchema,
      responses: {
        200: improvementPlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Close an active/extended PIP as completed_successful or completed_unsuccessful (terminal); unsuccessful notifies HR and the manager. Requires Idempotency-Key',
      metadata: routeMeta('improvement_plan:manage'),
    },

    cancelPlan: {
      method: 'POST',
      path: '/:planId/cancel',
      body: cancelPlanBodySchema,
      responses: {
        200: improvementPlanSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Cancel a PIP — a draft/pending one by its manager or HR (supersedes the pending approval), a running one by HR only; 409 once completed. Requires Idempotency-Key',
      metadata: routeMeta('improvement_plan:manage'),
    },

    listVerifierCandidates: {
      method: 'GET',
      path: '/:planId/verifier-candidates',
      responses: {
        200: z.object({ items: z.array(verifierCandidateSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        "The people allowed to verify this plan's milestones — the verifier picker's options",
      metadata: routeMeta('improvement_plan:manage'),
    },

    submitMilestoneEvidence: {
      method: 'POST',
      path: '/:planId/milestones/:milestoneId/evidence',
      body: submitMilestoneEvidenceBodySchema,
      responses: {
        200: improvementPlanMilestoneSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        "The plan's subject records evidence on an open milestone of their running plan — never marks it done. Requires Idempotency-Key",
      // The subject's own permission: every employee holds improvement_plan:read at self scope
      // (roles.ts), and never improvement_plan:manage. The handler then requires caller = subject.
      metadata: routeMeta('improvement_plan:read'),
    },

    verifyMilestone: {
      method: 'POST',
      path: '/:planId/milestones/:milestoneId/verify',
      body: verifyMilestoneBodySchema,
      responses: {
        200: improvementPlanMilestoneSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Mark an open milestone done — only its assigned verifier (or, with none assigned, an allowed verifier, who becomes it); the verifier is the caller, server-set. Requires Idempotency-Key',
      metadata: routeMeta('improvement_plan:manage'),
    },
  },
  { pathPrefix: '/improvement-plans' },
);
